import { Divider, Tooltip } from 'antd';
import { useEffect, useMemo, useState } from 'react';
import styled, { css } from 'styled-components';
import { v4 } from 'uuid';

import { CrossSvg } from '../../../assets/icons';
import { AlignStandardsSvg } from '../../../assets/icons/AlignStandardsSvg';
import FoldedHandsSVG from '../../../assets/icons/FoldedHands';
import { MagicWandSvg } from '../../../assets/icons/MagicWandSvg';
import theme from '../../../assets/theme';
import useAccessBadge from '../../../hooks/useAccessBadge';
import { useBreakPoint } from '../../../hooks/useBreakPoint';
import useConsumeGenericGeneratorAsNewPresentation from '../../../hooks/useConsumeGenericGeneratorAsNewPresentation';
import {
  saveLocalDynamicLessonState,
  useDynamicLessonStateContext,
} from '../../../hooks/useCreateLessonState';
import useDebounce from '../../../hooks/useDebounce';
import { useMetrics } from '../../../hooks/useMetrics';
import useRouterMatch from '../../../hooks/useRouterMatch';
import { Standards } from '../../../services/backendService/types';
import {
  ContentCategory,
  ExamplePrompt,
  getRandomExamples,
  lessonCategories,
  SubjectOption,
  subjectOptionMapper,
  subjectOptionMapperV2,
  supportedLanguages,
  writingSkills,
} from '../../../utils/createLessonPageUtils';
import { TextArea } from '../../_atoms/_TextArea';
import { Button } from '../../_atoms/Button';
import { IconWrapper } from '../../_atoms/IconWrapper';
import BaseModal from '../../_molecules/BaseModal';
import ShowLessonCreationProgressBar from '../../_molecules/ProgressBar/ShowLessonCreationProgressBar';
import { CategoryList } from './CategorySelector';
import ExtraDetailsForm from './components/ExtraDetailsForm';
import { FloatingSelect } from './components/FloatingSelect';
import ProgressBarDynamicLesson from './components/ProgressBarDynamicLesson';
import { SubPageLayout } from './CreatePage';
import { FindCategorizedLessonsTab } from './FindCategorizedLessonsTab';
import { MobileCategoryList } from './MobileCategoryList';
import { StandardsPicker } from './StandardsPicker';
import { CreateWithAIFormState } from './types';

export const gradeOptions = [
  { value: 'Kindergarten', displayName: 'K' },
  { value: '1st', displayName: '1st grade' },
  { value: '2nd', displayName: '2nd grade' },
  { value: '3rd', displayName: '3rd grade' },
  { value: '4th', displayName: '4th grade' },
  { value: '5th', displayName: '5th grade' },
  { value: '6th', displayName: '6th grade' },
  { value: '7th', displayName: '7th grade' },
  { value: '8th', displayName: '8th grade' },
  { value: '9th', displayName: '9th grade' },
  { value: '10th', displayName: '10th grade' },
  { value: '11th', displayName: '11th grade' },
  { value: '12th', displayName: '12th+ grade' },
];
export const AdditionalCriteriaInput = ({
  value,
  setCurrentAdditionalCriteria,
}: {
  value: string | undefined;
  setCurrentAdditionalCriteria: (value: string | undefined) => void;
}) => {
  const isLargerThan600 = useBreakPoint(600);
  const dispatchAndSync = useDynamicLessonStateContext(c => c.dispatchAndSync);

  return (
    <>
      <TextArea
        rows={isLargerThan600 ? 3 : 6}
        textAreaStyled={{
          boxShadow: theme.effects.floatingCreatorElementShadow,
          border: 'none',
        }}
        style={{
          width: '100%',
        }}
        value={value}
        placeholder={
          'Include an additional brain break… Make it 15 slides… Make it relevant for my students by…'
        }
        onChange={e => setCurrentAdditionalCriteria(e.target.value)}
        maxLength={6000} //TODO how many characters should we allow?
      />
    </>
  );
};
export const TopicInput = ({
  prompt,
  handleNewPrompt,
  placeholderText,
}: {
  prompt: string | undefined;
  handleNewPrompt: (prompt: string) => void;
  placeholderText?: string;
}) => {
  const isLargerThan600 = useBreakPoint(600);
  return (
    <>
      <TextArea
        rows={isLargerThan600 ? 3 : 6}
        textAreaStyled={{
          boxShadow: theme.effects.floatingCreatorElementShadow,
          border: 'none',
        }}
        style={{
          width: '100%',
        }}
        value={prompt}
        placeholder={
          placeholderText
            ? placeholderText
            : 'Add your learning objective, the topic, or content. Be explicit! \nE.g Engaging review with real-life examples of... Three practice questions for the skill... Make it 8 slides...'
        }
        onChange={e => handleNewPrompt(e.target.value)}
        maxLength={6000} //TODO how many characters should we allow?
      />
    </>
  );
};

const AutoFillSuggestions = ({
  handleNewPrompt,
  examples,
}: {
  handleNewPrompt: (prompt: string) => void;
  examples: ExamplePrompt[];
}) => {
  const metrics = useMetrics();
  const dispatchAndSync = useDynamicLessonStateContext(c => c.dispatchAndSync);
  const isLargerThan700 = useBreakPoint(700);
  const isLargerThan600 = useBreakPoint(600);
  const indexToColor = (index: number) => {
    switch (index) {
      case 0:
        return theme.colors.blue;
      case 1:
        return theme.colors.green;
      case 2:
        return theme.colors.orange;
      case 3:
        return theme.colors.purple;
      default:
        return theme.colors.blue;
    }
  };
  //at 700px only show 3 examples & 2 at 600px
  const examplescutdown = useMemo(() => {
    if (!isLargerThan700 && isLargerThan600) {
      return examples.slice(0, 3);
    }

    if (!isLargerThan700 && !isLargerThan600) {
      return examples.slice(0, 2);
    }
    return examples;
  }, [examples, isLargerThan600, isLargerThan700]);

  const handleExampleSelected = (example: ExamplePrompt) => {
    metrics.logEvent('CreatePage.ExamplePromptSelected', {
      exampleTitle: example.title,
    });
    handleNewPrompt(example.topicInput);
    if (example.customizationInput)
      dispatchAndSync({
        type: 'SET_ADDITIONAL_CRITERIA',
        payload: example.customizationInput,
      });
  };

  return (
    <ButtonsContainer>
      <StyledDivider>Example Prompts</StyledDivider>
      {examplescutdown.map((example, index) => (
        <ExampleButton
          onClick={() => handleExampleSelected(example)}
          $color={indexToColor(index)}
          icon={example.icon}
          key={index}
        >
          {example.title}
        </ExampleButton>
      ))}
    </ButtonsContainer>
  );
};

const ButtonsContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr 1fr 1fr;
  gap: 8px;
  width: 100%;
  max-width: 900px;
  margin: 0 auto;

  @media (max-width: 700px) {
    grid-template-columns: 1fr 1fr 1fr;
  }

  @media (max-width: 600px) {
    grid-template-columns: 1fr 1fr;
  }

  @media (max-width: 400px) {
    display: none;
  }
`;

const ExampleButton = styled(Button)<{ $color: string }>`
  margin-right: 8px;
  margin-bottom: 8px;
  border-radius: 5px;
  width: 100%;
  display: flex;
  flex-direction: column;
  justify-content: start;
  align-items: start;
  border: 1px solid #ffa154;
  color: ${theme.colors.black};
  background-color: ${theme.colors.white};
  gap: 8px;
  box-shadow: 0px 0px 10px #ffa15450;

  &:focus,
  &:active,
  &:hover {
    color: ${theme.colors.black};
    background-color: ${theme.colors.greyLight};
    border: 1px solid #ffa154;
  }

  svg {
    color: ${props => props.$color};
    height: 20px;
    width: 20px;
  }

  span {
    font-size: 12px;
    text-wrap: wrap;
    text-align: left;
    color: ${theme.colors.iconGrey};
  }
`;

const GenerateDynamicLessonForm = ({
  hasPreFilledDetails,
  title,
  setShowLoader,
  showLoader,
  hasNewBlockAccess,
}: {
  hasPreFilledDetails: boolean;
  title: string;
  setShowLoader: (d: boolean) => void;
  showLoader: boolean;
  hasNewBlockAccess: boolean;
}) => {
  const dispatchAndSync = useDynamicLessonStateContext(c => c.dispatchAndSync);
  const dynamicLessonState = useDynamicLessonStateContext(c => c.dynamicLessonState);

  const selectedStandard = useDynamicLessonStateContext(
    c => c.dynamicLessonState.selectedStandard,
  );
  const grade = useDynamicLessonStateContext(c => c.dynamicLessonState.grade);

  const languages = useDynamicLessonStateContext(c => c.dynamicLessonState.languages);
  const prompt = useDynamicLessonStateContext(c => c.dynamicLessonState.prompt);
  const uploadedFile = useDynamicLessonStateContext(
    c => c.dynamicLessonState.uploadedFile,
  );
  const lessonIntent = useDynamicLessonStateContext(
    c => c.dynamicLessonState.lessonIntent,
  );
  const selectedSubject = useDynamicLessonStateContext(c => c.dynamicLessonState.subject);
  const additionalCriteria = useDynamicLessonStateContext(
    c => c.dynamicLessonState.additionalCriteria,
  );
  const { tenantId, router } = useRouterMatch();
  const subjectOptions = hasNewBlockAccess ? subjectOptionMapperV2 : subjectOptionMapper;

  const [examples, _] = useState<ExamplePrompt[] | undefined>(
    getRandomExamples(selectedSubject || undefined),
  );
  const {
    consumeGenerator,
    isLoading: isGeneratingLesson,
    isError,
    data,
    error,
  } = useConsumeGenericGeneratorAsNewPresentation(tenantId);
  const metrics = useMetrics();
  const [helpFillForm, setHelpFillForm] = useState(false);
  const { accessBadge: userAccessBadge } = useAccessBadge();
  const presetState = userAccessBadge?.organization?.region;
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [currentPrompt, setCurrentPrompt] = useState<string | undefined>(prompt);
  const debouncedPrompt = useDebounce(currentPrompt, 1000);
  const [currentAdditionalCriteria, setCurrentAdditionalCriteria] = useState<
    string | undefined
  >(additionalCriteria);
  const debouncedAdditionalCriteria = useDebounce(currentAdditionalCriteria, 1000);
  const tenantSubjects = userAccessBadge?.user.profileInfo?.subjects;
  const tenantGrades = userAccessBadge?.user.profileInfo?.grades;

  useEffect(() => {
    if (isGeneratingLesson || data) {
      setShowLoader(true);
      return;
    }
    {
      setShowLoader(false);
      return;
    }
  }, [data, isGeneratingLesson, setShowLoader]);

  useEffect(() => {
    if (debouncedPrompt !== undefined) {
      dispatchAndSync({ type: 'ON_PROMPT_CHANGE', payload: currentPrompt });
    }
  }, [debouncedPrompt]);
  useEffect(() => {
    if (debouncedAdditionalCriteria !== undefined) {
      dispatchAndSync({
        type: 'SET_ADDITIONAL_CRITERIA',
        payload: currentAdditionalCriteria,
      });
    }
  }, [debouncedAdditionalCriteria]);

  useEffect(() => {
    if (data) {
      const blockLabels = (data.content.slides || [])
        .filter((value, index, self) => self.indexOf(value) === index)
        .map(slide => slide.blockId?.split('-')[0])
        .filter(Boolean);

      metrics.logEvent('CreatePage.Generator.Consume.FullLessonSuccessRedirect', {
        generatorId: 'genericFullLesson',
        generatorName: 'genericFullLesson',
        standard: selectedStandard?.id,
        subject: data.metadata?.subject,
        grade: grade,
        generatedLessonId: data.id,
        tenantSubjects: tenantSubjects,
        language: languages[0],
        tenantGrades: tenantGrades,
        schoolState: presetState,
        prompt: [currentPrompt || '', currentAdditionalCriteria].join('\n\n'),
        skeleton: data.metadata?.skeleton,
        blocks: blockLabels,
      });

      router.push(`/${tenantId}/lessons/${data.id}/edit`);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [router, tenantId, data]);

  useEffect(() => {
    if (isError) {
      setShowErrorModal(true);
    }
  }, [isError]);

  const [isStandardsSelectorOpen, setIsStandardsSelectorOpen] = useState<{
    isOpen: boolean;
    subject?: { id: string; label: string };
    canSwitchSubject?: boolean;
  }>({ isOpen: false });
  const isDesktop = useBreakPoint(700);
  const isLargerThan850 = useBreakPoint(850);

  const isGetLessonButtonDisabled = useMemo(() => {
    return !currentPrompt || !grade;
  }, [currentPrompt, grade]);

  const formErrorText = useMemo(() => {
    if (!currentPrompt) {
      return `Don't forget to enter your learning objective, topic or content`;
    }
    if (!grade) {
      return `Don't forget to choose a grade`;
    }
    if (!uploadedFile) {
      return `Don't forget to upload a presentation`;
    }
    return '';
  }, [currentPrompt, grade, uploadedFile]);

  const handleGetLesson = () => {
    if (isGetLessonButtonDisabled) {
      setHelpFillForm(true);
      //scroll user to bottom so they see error message
      const scrollElement = document.querySelector('.scrollArea');

      if (scrollElement && scrollElement.lastElementChild) {
        scrollElement.lastElementChild.scrollIntoView({
          behavior: 'smooth',
          block: 'end',
        });
      }
      return;
    }
    if (currentPrompt && grade) {
      saveLocalDynamicLessonState({ languages: languages, grade });
      const constructPrompt = () => {
        const basePrompt = currentPrompt ? currentPrompt : ''; // Default to an empty string if no prompt is provided

        const hasAdditionalCriteria = currentAdditionalCriteria;
        const hasSelectedStandard = selectedStandard;

        if (hasAdditionalCriteria && hasSelectedStandard) {
          return [
            basePrompt,
            `${selectedStandard.label} ${selectedStandard.description}`,
            currentAdditionalCriteria,
          ].join('\n\n ');
        }
        if (hasAdditionalCriteria) {
          return [basePrompt, currentAdditionalCriteria].join('\n\n ');
        }
        if (hasSelectedStandard) {
          return [
            basePrompt,
            `${selectedStandard.label} ${selectedStandard.description}`,
          ].join('\n\n ');
        }
        return basePrompt;
      };

      const subject =
        selectedSubject === 'socialStudies' ? 'socialStudies' : selectedSubject;

      const finalPromptData = {
        id: v4(),
        dynamicLessonState,
        createdAt: new Date(),
      };

      try {
        if (typeof window !== 'undefined') {
          const existingRecentPrompts = JSON.parse(
            localStorage.getItem('@curipod_v1_createWithAI_recentPrompts') || '[]',
          );
          localStorage.setItem(
            '@curipod_v1_createWithAI_recentPrompts',
            //max 50 recent prompts saved to local storage
            JSON.stringify([finalPromptData, ...existingRecentPrompts].slice(0, 50)),
          );
        }
      } catch (error) {
        console.error('Error saving recent prompts', error);
      }

      consumeGenerator({
        prompt: constructPrompt(),
        grade: grade,
        subject: subject,
        lessonCategory: lessonIntent?.id,
        // languages:  [language, ...bilingualLanguages],
        languages: [...languages],
      });
    }
  };
  const handleStandardSelected = ({
    grade,
    subject,
    strand,
  }: {
    subject: string;
    grade: string;
    strand?: Standards;
  }) => {
    setIsStandardsSelectorOpen({ isOpen: false, canSwitchSubject: false });
    dispatchAndSync({
      type: 'SET_SELECTED_STANDARD',
      payload: { selectedStandard: strand, subject, grade },
    });
  };

  return (
    <>
      {' '}
      {showLoader && (
        <LoaderContainer>
          <ShowLessonCreationProgressBar />
        </LoaderContainer>
      )}
      <BaseModal visible={showErrorModal} onCancel={() => setShowErrorModal(false)}>
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            flexDirection: 'column',
          }}
        >
          <IconWrapper height="120px" width="120px" style={{ marginTop: 20 }}>
            <FoldedHandsSVG />
          </IconWrapper>

          <ErrorMessage>
            <>
              <ErrorMessage style={{ fontWeight: 800 }}>
                Uh-oh! Something went wrong on our end
              </ErrorMessage>

              <ErrorMessage style={{ fontSize: '16px' }}>
                {error?.customMessage ||
                  'Please try again or contact support if the issue persists'}
              </ErrorMessage>
            </>
          </ErrorMessage>
          <Button
            styles={{ marginTop: 20 }}
            width="120px"
            onClick={() => setShowErrorModal(false)}
          >
            Ok
          </Button>
        </div>
      </BaseModal>
      {!showLoader && (
        <>
          {title && (
            <h1
              style={{
                width: '100%',
                textAlign: 'center',
                fontWeight: 800,
                fontSize: isDesktop ? 36 : 22,
                padding: '0 10px',
                marginBottom: 0,
              }}
            >
              {title}
            </h1>
          )}
          <FormContainer>
            {isStandardsSelectorOpen.isOpen && isStandardsSelectorOpen.subject && (
              <BaseModal
                visible
                noPadding
                onCancel={() =>
                  setIsStandardsSelectorOpen({ isOpen: false, canSwitchSubject: false })
                }
                width={isDesktop ? '80vw' : '95vw'}
                centered
              >
                <StandardsPicker
                  onStandardSelected={handleStandardSelected}
                  presetState={presetState || 'TX'}
                  presetGrade={grade}
                  presetSubject={isStandardsSelectorOpen.subject}
                  canSwitchSubject={isStandardsSelectorOpen.canSwitchSubject}
                />
              </BaseModal>
            )}
            <div
              style={{
                display: 'flex',
                flexDirection: hasPreFilledDetails ? 'column-reverse' : 'column',
                gap: 20,
              }}
            >
              <div>
                <p style={{ fontSize: 16, marginBottom: 0 }}>
                  {hasPreFilledDetails
                    ? 'Your lesson will teach...'
                    : selectedSubject === 'math' ||
                      selectedSubject === 'science' ||
                      selectedSubject === 'socialStudies'
                    ? 'What content do you want to teach?'
                    : 'What are you teaching?'}
                </p>
                <TopicInput
                  prompt={currentPrompt}
                  handleNewPrompt={(prompt: string) => setCurrentPrompt(prompt)}
                  placeholderText={
                    selectedSubject === 'math'
                      ? 'Practice simplifying improper fractions… Understand the concept of a ratio… Review constant rate of change… '
                      : selectedSubject === 'science'
                      ? 'Describe the phases of… Distinguish between… Analyze the impact of…'
                      : selectedSubject === 'socialStudies'
                      ? 'The 3 main causes of… Compare and contrast the ideas of… Impact of Enlightenment thinking on…'
                      : selectedSubject === 'worldLanguages'
                      ? 'Introduce past perfect… Vocabulary related to… Compare traditions in the US and…'
                      : 'Add your learning objective, the topic, or content. Be explicit! \nE.g Engaging review with real-life examples of... Three practice questions for the skill... Make it 8 slides...'
                  }
                />
                <AlignToStandardsRow>
                  {selectedStandard && (
                    <div
                      style={{
                        flex: '1',
                      }}
                    >
                      <Label style={{ marginBottom: 0, display: 'flex' }}>
                        <IconWrapper
                          height="8px"
                          width="8px"
                          style={{ marginRight: 10 }}
                          onClick={() => {
                            metrics.logEvent('CreatePage.standardSelectorCleared');
                            dispatchAndSync({ type: 'CLEAR_SELECTED_STANDARD' });
                          }}
                        >
                          <CrossSvg />
                        </IconWrapper>
                        {selectedStandard.label}
                      </Label>
                    </div>
                  )}
                  {(selectedSubject === 'ela' ||
                    selectedSubject === 'socialStudies' ||
                    selectedSubject === 'science' ||
                    selectedSubject === 'math') && (
                    <SingleSubjectStandardsButton
                      onClick={() => {
                        const selectedSubjectToSubject =
                          selectedSubject === 'socialStudies'
                            ? 'socialStudies'
                            : selectedSubject.toLowerCase().replace(' ', '-');

                        metrics.logEvent('CreatePage.standardSelectorOpened', {
                          subject: selectedSubjectToSubject,
                          grade: grade,
                          prompt: currentPrompt,
                          schoolState: presetState,
                        });
                        setIsStandardsSelectorOpen({
                          isOpen: true,
                          canSwitchSubject: false,
                          subject: {
                            id: selectedSubjectToSubject,
                            label: selectedSubject,
                          },
                        });
                      }}
                      icon={
                        <IconWrapper height="14px" width="14px">
                          <AlignStandardsSvg />
                        </IconWrapper>
                      }
                    >
                      Align to standards
                    </SingleSubjectStandardsButton>
                  )}
                  {(selectedSubject === 'advancedPrompting' ||
                    selectedSubject === 'worldLanguages' ||
                    selectedSubject === 'otherSubjects') && (
                    <>
                      <SingleSubjectStandardsButton
                        onClick={() => {
                          metrics.logEvent('CreatePage.standardSelectorOpened', {
                            subject: 'ela',
                            grade: grade,
                            prompt: currentPrompt,
                            schoolState: presetState,
                          });
                          setIsStandardsSelectorOpen({
                            isOpen: true,
                            canSwitchSubject: true,
                            subject: {
                              id: 'ela',
                              label: 'ela', //Default to ELA when we dont know the subject
                            },
                          });
                        }}
                        icon={
                          <IconWrapper height="14px" width="14px">
                            <AlignStandardsSvg />
                          </IconWrapper>
                        }
                      >
                        Align to standards
                      </SingleSubjectStandardsButton>
                    </>
                  )}
                </AlignToStandardsRow>
              </div>
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: isDesktop ? '1fr 2fr' : '1fr',
                  alignItems: isDesktop ? 'center' : 'start',
                  gap: 20,
                  flexDirection: isDesktop ? 'row' : 'column',
                }}
              >
                <FloatingSelect
                  label="Grade"
                  width={isLargerThan850 ? undefined : '100%'}
                  value={grade}
                  options={gradeOptions}
                  onSelect={(grade: string) =>
                    dispatchAndSync({ type: 'SET_GRADE', payload: grade })
                  }
                />

                <FloatingSelect
                  label="Language"
                  width={isLargerThan850 ? undefined : '100%'}
                  value={languages[0]}
                  options={supportedLanguages}
                  onSelect={(language: string) =>
                    dispatchAndSync({ type: 'SET_LANGUAGE', payload: language })
                  }
                />
              </div>
              {selectedSubject === 'advancedPrompting' && (
                <div>
                  <p style={{ fontSize: 16, margin: 0 }}>
                    Additional criteria (optional)
                  </p>
                  <AdditionalCriteriaInput
                    value={currentAdditionalCriteria}
                    setCurrentAdditionalCriteria={setCurrentAdditionalCriteria}
                  />
                </div>
              )}
            </div>
            <div
              style={{
                margin: '20px auto 10px auto',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                width: '100%',
              }}
            >
              <GetLessonButton
                type="primary"
                width={isDesktop ? '200px' : '100%'}
                onClick={() => {
                  handleGetLesson();
                }}
                loading={isGeneratingLesson}
                $disabled={isGetLessonButtonDisabled}
                icon={
                  <IconWrapper height="20px" width="20px">
                    <MagicWandSvg />
                  </IconWrapper>
                }
              >
                Get Lesson
              </GetLessonButton>
              {isGetLessonButtonDisabled && helpFillForm && (
                <FormErrorText>{formErrorText}</FormErrorText>
              )}
            </div>
          </FormContainer>
        </>
      )}
      {!showLoader && !hasPreFilledDetails && (
        <>
          {/* if there is recent prompt id, we hide the examples shown at the bottom */}
          <AutoFillSuggestions
            handleNewPrompt={(prompt: string) => {
              setCurrentPrompt(prompt);
            }}
            examples={examples || []}
          />
        </>
      )}
    </>
  );
};

const SingleSubjectStandardsButton = styled(Button)`
  background-color: ${theme.colors.paleBeige};
  color: ${theme.colors.black};
  box-shadow: ${theme.effects.magicFloatingShadow};
  border-radius: 5px;
  padding: 5px 10px;
  font-size: 16px;
  font-weight: 600;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 5px;

  &:focus,
  &:active,
  &:hover {
    background-color: ${theme.colors.white};
    color: ${theme.colors.black};
  }
`;

const StyledDivider = styled(Divider)`
  max-width: 900px !important ;
  margin: 26px auto !important;
  min-width: unset !important;
  grid-column: 1 / -1;
  &.ant-divider-horizontal.ant-divider-with-text {
    font-size: 14px;
    color: ${theme.colors.iconGrey};
  }

  &.ant-divider-horizontal.ant-divider-with-text::before,
  &.ant-divider-horizontal.ant-divider-with-text::after {
    top: 0% !important;
    border-color: ${theme.colors.iconGrey};
  }
`;

export const AnyContentOrSkillsTab = ({
  handleSelectSubject,
  hasNewBlockAccess,
}: {
  handleSelectSubject: (subject: string) => void;
  hasNewBlockAccess: boolean;
}) => {
  const dispatchAndSync = useDynamicLessonStateContext(c => c.dispatchAndSync);

  const metrics = useMetrics();
  const isLargerThan600 = useBreakPoint(600);
  const [showLoader, setShowLoader] = useState(false);
  const subjectOptions = hasNewBlockAccess ? subjectOptionMapperV2 : subjectOptionMapper;

  const selectedPage = useDynamicLessonStateContext(
    c => c.dynamicLessonState.selectedPage,
  );
  const lessonIntent = useDynamicLessonStateContext(
    c => c.dynamicLessonState.lessonIntent,
  );
  const subject = useDynamicLessonStateContext(c => c.dynamicLessonState.subject);

  const isFinalPage = useMemo(() => {
    const shouldShowFinalPage =
      subject === 'advancedPrompting' ||
      ((subject === 'worldLanguages' || subject === 'otherSubjects') &&
        !hasNewBlockAccess);
    if (shouldShowFinalPage) return true;

    const requiresMoreDetails = lessonIntent?.hasMoreDetails;
    // example: step 4 is final if lessonIntent hasMoreDetails
    const totalSteps = !requiresMoreDetails ? 3 : 4;

    return selectedPage >= totalSteps;
  }, [subject, hasNewBlockAccess, lessonIntent?.hasMoreDetails, selectedPage]);

  const hasPreFilledDetails = useMemo(() => {
    if (!subject || !lessonIntent) return false;
    const foundlesson = subjectOptions[subject]?.lessonIntents?.find(
      type => lessonIntent?.id === type.id,
    );
    return foundlesson?.hasMoreDetails || false;
  }, [lessonIntent, subjectOptions, subject]);

  const numberOfSteps = useMemo(() => {
    if (!subject) return 2;
    const hasMultiplelessonIntents =
      (subjectOptions[subject]?.lessonIntents?.length ?? 0) > 1;

    const isSubjectWithExtraStep = subjectOptions[subject]?.lessonIntents?.some(
      lessonIntent => lessonIntent.hasMoreDetails,
    );

    if (isSubjectWithExtraStep) {
      return 4;
    }

    if (hasMultiplelessonIntents) {
      return 3;
    }

    return 2;
  }, [subject, subjectOptions]);

  const formProgress = useMemo(() => {
    //If the subject has an extra step we have 4 total steos
    //if it is the final step we are at 100%
    if (isFinalPage) {
      return 100;
    }
    if (!numberOfSteps || selectedPage <= 1) {
      return 10;
    }

    const rawProgress = Math.floor(((selectedPage - 1) / (numberOfSteps - 1.1)) * 100);

    return Math.max(10, Math.min(100, rawProgress));
  }, [isFinalPage, selectedPage, numberOfSteps]);

  const isDesktop = useBreakPoint(700);
  const generatorFilterCategory = useDynamicLessonStateContext(
    c => c.dynamicLessonState.generatorFilterCategory,
  );

  const onSelectGeneratorFilterCategory = (categoryId: string) => {
    metrics.logEvent('CreatePage.SelectCategory.Clicked', {
      selectedCategory: categoryId,
      selectedLessonCategory: 'writing_skills',
    });
    dispatchAndSync({
      type: 'SET_GENERATOR_FILTER_CATEGORY',
      payload: categoryId as string,
    });
  };

  return (
    <>
      {!showLoader && isLargerThan600 && (
        <ProgressBarDynamicLesson progress={formProgress} numberOfSteps={numberOfSteps} />
      )}
      {selectedPage === 1 && !isFinalPage && (
        <>
          {/*Select subject*/}
          <SubPageLayout pageTitle="Generate a Lesson With AI">
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'space-between',
                width: '100%',
                height: '100%',
                flexGrow: 1,
                padding: 20,
                gap: 20,
              }}
            >
              <CategoriesContainer>
                {Object.keys(subjectOptions).map((subject, key) => (
                  <CategoryButton
                    $isComingSoon={false}
                    onClick={() => {
                      metrics.logEvent('CreatePage.SubjectSelected', {
                        subject,
                      });
                      handleSelectSubject(subject);
                    }}
                    key={key}
                  >
                    {subjectOptions[subject].displayName}
                  </CategoryButton>
                ))}
              </CategoriesContainer>
              <Button
                styles={{ justifySelf: 'end' }}
                type="linkStyle"
                onClick={() => {
                  metrics.logEvent('CreatePage.AdvancedOwnPromptClicked');
                  dispatchAndSync({ type: 'SET_ADVANCED_PROMPT' });
                }}
              >
                Advanced - write your own prompt
              </Button>
            </div>
          </SubPageLayout>
        </>
      )}
      {selectedPage === 2 && !isFinalPage && (
        <>
          {/*Select category - ELA specific*/}
          <SubPageLayout pageTitle="What are you teaching?">
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'space-between',
                width: '100%',
                flexGrow: 1,
                padding: 20,
                gap: 20,
              }}
            >
              <CategoriesContainer>
                {subject &&
                  subjectOptions[subject]?.lessonIntents?.map(
                    (lessonIntent: SubjectOption) => (
                      <Tooltip
                        key={lessonIntent.id}
                        title={lessonIntent.comingSoon ? 'Coming Soon!' : ''}
                        mouseEnterDelay={0.5}
                      >
                        <CategoryButton
                          $isComingSoon={lessonIntent.comingSoon || false}
                          onClick={() => {
                            if (lessonIntent.comingSoon) {
                              metrics.logEvent(
                                'CreatePage.lessonIntentComingSoonClicked',
                                {
                                  lessonIntent: lessonIntent.id,
                                },
                              );

                              return;
                            }
                            if (lessonIntent.hasMoreDetails) {
                              metrics.logEvent('CreatePage.lessonIntentSelected', {
                                lessonIntent: lessonIntent.id,
                              });
                              dispatchAndSync({
                                type: 'SET_LESSON_INTENT',
                                payload: lessonIntent,
                                isDesktop: isDesktop,
                              });
                              return;
                            }
                            //For now all other lesson types take you to generate

                            dispatchAndSync({
                              type: 'SET_LESSON_INTENT',
                              payload: lessonIntent,
                              isDesktop: isDesktop,
                            });
                          }}
                        >
                          {lessonIntent.displayName}
                        </CategoryButton>
                      </Tooltip>
                    ),
                  )}
              </CategoriesContainer>
            </div>
          </SubPageLayout>
        </>
      )}
      {!isFinalPage && lessonIntent?.hasMoreDetails && selectedPage === 3 && (
        <>
          <SubPageLayout pageTitle="A Few More Details">
            <TabContainer>
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  width: '100%',
                  gap: 20,
                }}
              >
                <ExtraDetailsForm
                  lessonIntent={lessonIntent}
                  hasNewBlockAccess={hasNewBlockAccess}
                  subject={subject}
                />
              </div>
            </TabContainer>
          </SubPageLayout>
        </>
      )}
      {isFinalPage && lessonIntent?.id === 'quickWrites' && (
        <>
          {isDesktop ? (
            <SubPageLayout pageTitle="Choose your quick write lesson">
              <TabContainer>
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '20px',
                    width: '100%',
                    alignItems: 'center',
                  }}
                >
                  <CategoryList
                    handleSelectCategory={(categoryId: string) => {
                      onSelectGeneratorFilterCategory(categoryId);
                    }}
                    visibleCategories={writingSkills.categories || []}
                    activeCategoryId={generatorFilterCategory}
                  />
                  {generatorFilterCategory && (
                    <FindCategorizedLessonsTab
                      lessonCategoryId={generatorFilterCategory}
                      selectedCategory={generatorFilterCategory}
                    />
                  )}
                </div>
              </TabContainer>
            </SubPageLayout>
          ) : (
            <>
              {!generatorFilterCategory ? (
                <SubPageLayout pageTitle="Choose a Category">
                  <TabContainer>
                    <MobileCategoryList
                      handleSelectCategory={(categoryId: string) => {
                        onSelectGeneratorFilterCategory(categoryId);
                      }}
                      visibleCategories={writingSkills.categories || []}
                    />
                  </TabContainer>
                </SubPageLayout>
              ) : (
                <SubPageLayout pageTitle="Quick Writes">
                  <TabContainer>
                    <FindCategorizedLessonsTab
                      categoryTitle={
                        writingSkills.categories?.find(
                          (category: ContentCategory) =>
                            category.id === generatorFilterCategory,
                        )?.title || ''
                      }
                      lessonCategoryId={generatorFilterCategory}
                      selectedCategory={generatorFilterCategory}
                    />
                  </TabContainer>
                </SubPageLayout>
              )}
            </>
          )}
        </>
      )}
      {isFinalPage && lessonIntent?.id !== 'quickWrites' && (
        <>
          {/*Generate the lesson form */}
          <SubPageLayout>
            <TabContainer>
              <GenerateDynamicLessonForm
                title={
                  hasPreFilledDetails
                    ? 'Review the details'
                    : `Generate a${
                        !subject || subject === 'otherSubjects'
                          ? ''
                          : subject === 'ela'
                          ? 'n ELA'
                          : subjectOptions[subject]?.displayName
                          ? ` ${subjectOptions[subject]?.displayName} `
                          : ''
                      } Lesson`
                }
                hasPreFilledDetails={hasPreFilledDetails}
                setShowLoader={setShowLoader}
                showLoader={showLoader}
                hasNewBlockAccess={hasNewBlockAccess}
              />
            </TabContainer>
          </SubPageLayout>
        </>
      )}
    </>
  );
};

export const InputsWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const CategoriesContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 15px 10px;
  max-width: 900px;
  margin: 30px auto;
  justify-content: center;
`;

const CategoryButton = styled(Button)<{ $isComingSoon: boolean }>`
  color: ${theme.colors.black};
  background-color: ${theme.colors.paleBeige};
  box-shadow: ${theme.effects.magicFloatingShadow};
  border-radius: 30px;
  width: 280px;

  span {
    font-size: 22px;
  }

  &:hover,
  &:focus,
  &:active {
    background-color: ${theme.colors.white};
    box-shadow: ${theme.effects.magicFloatingShadowHovered};
    color: ${theme.colors.black};
  }

  ${props =>
    props.$isComingSoon &&
    css`
      color: ${theme.colors.black};
      background-color: ${theme.colors.paleBeige};
      box-shadow: ${theme.effects.magicFloatingShadow};

      &:hover,
      &:focus,
      &:active {
        color: ${theme.colors.black};
        background-color: ${theme.colors.paleBeige};
        box-shadow: ${theme.effects.magicFloatingShadow};
      }
    `}

  @media (max-width: 700px) {
    width: 200px;

    span {
      font-size: 18px;
    }
  }
`;

const Label = styled.p`
  font-size: 14px;
  padding: 2px 10px;
  border-radius: 5px;
  background: #cfede2;
  color: #177655;
  font-weight: 800;
  width: fit-content;
  margin-bottom: 5px;
  white-space: nowrap;
  box-shadow: ${theme.effects.floatingCreatorElementShadow};
`;
const ErrorMessage = styled.h4`
  font-weight: bold;
  width: 100%;
  text-align: center;
  margin-top: 20px;
  font-size: 18px;

  p {
    margin-top: 10px;
    font-size: 14px;
  }
`;

const FormErrorText = styled.p`
  color: ${theme.colors.red};
  font-size: 14px;
  margin: 0;
  margin-top: 10px;
  text-align: center;
  background-color: ${theme.colors.veryLightRed};
  border-radius: 10px;
  padding: 10px;
`;
const AlignToStandardsRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  flex-wrap: wrap;
  margin-top: 10px;
  font-size: 14px;
`;

const GetLessonButton = styled(Button)<{ $disabled: boolean }>`
  color: ${theme.colors.white};
  background-color: ${theme.colors.orange};
  box-shadow: ${theme.effects.magicFloatingShadow};
  margin: auto;

  ${props =>
    props.$disabled &&
    css`
      background-color: ${theme.colors.orangeDark};
      box-shadow: ${theme.effects.magicFloatingShadow};
      &:hover {
        background-color: ${theme.colors.orangeDark};
        box-shadow: ${theme.effects.magicFloatingShadow};
      }
    `}

  &:hover {
    background-color: ${theme.colors.orangeDark};
    box-shadow: ${theme.effects.magicFloatingShadowHovered};
  }

  &:focus,
  &:active {
    background-color: ${theme.colors.orangeDark};
    box-shadow: ${theme.effects.defaultShadow};
  }
`;
const LoaderContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  flex-direction: column;
`;

export const FormContainer = styled.div`
  padding-top: 10px;
  margin: auto;
  width: 100%;
  max-width: 900px;
  display: grid;
  gap: 20px;

  @media (max-width: 1000px) {
    width: 100%;
    padding-left: 20px;
    padding-right: 20px;
  }
`;

const TabContainer = styled.div`
  padding-top: 0px;
  margin: 0px auto 50px auto;
  width: 80%;
  display: grid;

  @media (max-width: 1000px) {
    width: 100%;
    padding-left: 20px;
    padding-right: 20px;
  }

  @media (max-width: 700px) {
    padding-top: 0px;
  }
`;
